import {ContactVehicleRepo} from "../api/vehicle/contact-vehicle.repo";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText} from "./duckdb.helper";
import {inject, Injectable} from "@angular/core";
import {SearchVehicleRes} from "../dto/vehicle/search-vehicle.res";
import {map} from "rxjs/operators";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {LoggingService} from "../../services/logger/logging.service";
import {VehicleDto} from "../dto/vehicle/vehicle.dto";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";

@Injectable({providedIn: 'root'})
export class ContactVehicleDuckDb extends ContactVehicleRepo {

  private readonly duck = inject(DuckDbHelper);
  private readonly log = inject(LoggingService);

  public override getById(_org: OrgDto, contactId: string, vehicleId: string): Observable<WrapperDto<VehicleDto>> {
    const selectVehicle = `
      SELECT v.id                                                           AS id,
             v.license_plate                                                AS licensePlate,
             v.manufacturer_code_number                                     AS manufacturerCodeNumber,
             v.type_code_number                                             AS typeCodeNumber,
             v.volume_key_number                                            AS volumeKeyNumber,
             v.vin                                                          AS vin,
             v.first_registration                                           AS firstRegistration,
             v.performance                                                  AS performanceInKw,
             v.construction_date                                            AS constructionDate,
             v.displacement                                                 AS displacementInCcm,
             v.engine_code                                                  AS engineCode,
             v.color_code                                                   AS colorCode,
             v.next_technical_inspection                                    AS nextTechnicalInspection,
             v.next_emissions_test                                          AS nextEmissionsTest,
             v.notes                                                        AS notes,
             v.engine_id                                                    AS engine,
             v.type_id                                                      AS type,
             v.manufacturer_id                                              AS manufacturer,
             v.model_id                                                     AS model,
             v.variant_id                                                   AS variant,
             v.next_customer_service                                        AS nextCustomerService,
             v.next_tire_change                                             AS nextTireChange,
             v.engine_name                                                  AS engineName,
             v.type_name                                                    AS typeName,
             v.manufacturer_name                                            AS manufacturerName,
             v.model_name                                                   AS modelName,
             v.variant_name                                                 AS variantName,
             TRIM(COALESCE(c.firstname, '') || ' ' || COALESCE(c.name, '')) AS contactFullName,
             v.type_approval                                                AS typeApproval,
             v.registration_number                                          AS registrationNumber
      FROM vehicle v
             LEFT JOIN contact c ON c.id = v.contact_id
      WHERE v.id = ${sqlText(vehicleId)}
        AND c.id = ${sqlText(contactId)}
        AND v.belong = 'CONTACT';`;

    return this.duck.sql(selectVehicle).pipe(map((vehicleRes: any) => {
      if (DuckDbHelper.hasRes(vehicleRes)) {
        const vehicle = new VehicleDto(vehicleRes[0]);
        return DuckDbHelper.data('VehicleDto', vehicle);
      }
      return DuckDbHelper.data('VehicleDto', new VehicleDto());
    }));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchVehicleRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const query = this.buildQuery(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT v.id                                                           AS id,
             v.license_plate                                                AS licensePlate,
             v.vin                                                          AS vin,
             v.next_technical_inspection                                    AS nextTechnicalInspection,
             v.next_emissions_test                                          AS nextEmissionsTest,
             v.contact_id                                                   AS contactId,
             v.next_customer_service                                        AS nextCustomerService,
             v.next_tire_change                                             AS nextTireChange,
             v.manufacturer_name                                            AS manufacturerName,
             v.model_name                                                   AS modelName,
             TRIM(COALESCE(c.firstname, '') || ' ' || COALESCE(c.name, '')) AS contactFullName,
             v.type_approval                                                AS typeApproval,
             v.registration_number                                          AS registrationNumber,
             CONCAT(COALESCE(v.manufacturer_code_number, ''), COALESCE(v.type_code_number, ''),
                    COALESCE(v.volume_key_number, ''))                      AS codeNumber,
             COUNT(*)                                                          OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM vehicle v
             LEFT JOIN contact c ON c.id = v.contact_id
      WHERE ${isDefined(query) ? query : '1=1'}
        AND v.belong = 'CONTACT'
        AND ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res: any) => {
      const hasRes = isDefined(res) && res.length > 0;
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchVehicleRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchVehicleRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string {
    let field;
    switch (request.sortField) {
      // Die Typeahead-Suche (ContactVehicleService) sortiert nach 'number'; ein Fahrzeug hat keine Nummer.
      case 'number':
      case 'licensePlate':
        field = 'v.license_plate';
        break;
      case 'contactFullName':
        field = 'c.firstname';
        break;
      case 'codeNumber':
        field = 'v.manufacturer_code_number';
        break;
      case 'manufacturerName':
        field = 'v.manufacturer_name';
        break;
      case 'modelName':
        field = 'v.model_name';
        break;
      case 'vin':
        field = 'v.vin';
        break;
      case 'typeApproval':
        field = 'v.type_approval';
        break;
      case 'registrationNumber':
        field = 'v.registration_number';
        break;
      default:
        this.log.warn('Unhandled sort field "{}"', [request.sortField]);
        field = 'v.license_plate';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(v.license_plate ILIKE ${sqlLike(request.query)}
          OR c.name ILIKE ${sqlLike(request.query)}
          OR c.firstname ILIKE ${sqlLike(request.query)}
          OR v.manufacturer_code_number ILIKE ${sqlLike(request.query)}
          OR v.type_code_number ILIKE ${sqlLike(request.query)}
          OR v.volume_key_number ILIKE ${sqlLike(request.query)}
          OR v.model_name ILIKE ${sqlLike(request.query)}
          OR v.vin ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  private buildFilter(request: SearchRequest): string | undefined {
    const filters: string[] = [];
    // Kontextfilter aus der Route: Bei leerer Auswahl ist "nichts" richtig - "alles" waere die
    // Fahrzeugliste fremder Kontakte am eigenen Reiter. Siehe sqlText() zum blanken Token NULL.
    const contactIdFilter = request.filters.find((f: any) => f.id === 'contact-id');
    if (isDefined(contactIdFilter) && isDefined(contactIdFilter.selected)) {
      filters.push(contactIdFilter.selected.length > 0 ? `v.contact_id  = ${sqlText(contactIdFilter.selected[0])}` : '1=0');
    }
    return filters.length > 0 ? filters.join(' AND ') : undefined;
  }
}
